/** Two of the redesign's three signals (spec §4), kept apart from alarms,
 *  and both on the health monitor's own rule (backend intake/health.py).
 *
 *  Freshness is how old one value is against the station's expected
 *  interval, which the station summary carries (`expected_interval_s`,
 *  `silent_after_s`): on time, late (the latest expected reading is
 *  missing), or overdue once the monitor would call the station silent. It
 *  colours the Last Observation cell and the latest-reading tiles (D2). The
 *  expected interval allows for a pusher's cadence, an MDS logger's upload
 *  delay and a station collected only daily, which `poll_interval_s` alone
 *  does not, so it is read from the API and never recomputed here.
 *
 *  System status is data transmission only: Receiving or No data, the
 *  monitor's verdict. Late is never an alarm, and nothing here is one:
 *  alarms come from alert rules (phase 4). */
import type { Station, StationAlert } from '../api/types'
import { alertText, duration, severity } from './health'

export type Freshness = 'ok' | 'late' | 'over' | 'never'

/** Strict at both edges: a reading exactly one expected interval old is on
 *  time, exactly `silentAfterS` old is late; the monitor opens `silent`
 *  only past it. */
export function freshness(lastAt: string | null, expectedS: number, silentAfterS: number,
                          now: Date): Freshness {
  if (!lastAt) return 'never'
  const at = new Date(lastAt).getTime()
  if (Number.isNaN(at)) return 'never'
  const age = (now.getTime() - at) / 1000
  if (age > silentAfterS) return 'over'
  if (age > expectedS) return 'late'
  return 'ok'
}

export interface SystemStatus { status: 'receiving' | 'no_data' | 'not_checked'; note: string }

/** A value's freshness where silence means something: none for an
 *  event-driven channel (a tipping bucket is silent between tips by design)
 *  and none without the monitor's numbers. */
export function verdict(lastAt: string | null, expectedS: number | undefined,
                        silentAfterS: number | undefined, now: Date,
                        eventDriven: boolean): Freshness | null {
  if (eventDriven || !expectedS || !silentAfterS) return null
  return freshness(lastAt, expectedS, silentAfterS, now)
}

const RANK = { alarm: 0, warning: 1 } as const

/** The alert a reader should see first: alarms, then warnings. Information
 *  (quiet channels) never becomes the note; Explore lists it. */
function topAlert(alerts: StationAlert[]): StationAlert | undefined {
  return alerts
    .filter((a) => severity(a) === 'alarm' || severity(a) === 'warning')
    .sort((a, b) => RANK[severity(a) as 'alarm' | 'warning'] - RANK[severity(b) as 'alarm' | 'warning'])[0]
}

const DECOMMISSIONED = 'Decommissioned'
const COLLECTION_OFF = 'Collection off'
const NEVER_READ = 'Never read'
/** What a reader is told when the automatic checks have stopped: it says the
 *  checks are behind, where "Not checked" read as a task for them (PM-23). */
export const MONITORING_DELAYED = 'Monitoring delayed'

/** Receiving or No data, with the one sentence that says why.
 *
 *  No data exactly when the monitor's `silent` alert is open, or the
 *  station is decommissioned, has collection off, or was never read. It
 *  never reads the clock: a reading that looks old to the browser is not No
 *  data until the monitor says so, and a silent station is No data even if
 *  a reading has just arrived and the monitor has not closed the alert yet.
 *  The note's order: decommissioned, collection off, never read, the top
 *  alert's headline, then the interval the monitor expects. */
export function systemStatus(station: Station, checksStale = false): SystemStatus {
  if (!station.active) return { status: 'no_data', note: DECOMMISSIONED }
  if (!station.schedule_enabled) return { status: 'no_data', note: COLLECTION_OFF }
  if (!station.last_measurement_at) return { status: 'no_data', note: NEVER_READ }
  // Checks that stopped cannot vouch for a station: it reads "Monitoring delayed",
  // never Receiving (HANDBOOK, the health checks). The caller decides
  // staleness (lib/health checksStale), so this still reads no clock.
  if (checksStale) return { status: 'not_checked', note: MONITORING_DELAYED }

  const alerts = station.alerts ?? []
  const top = topAlert(alerts)
  const expected = station.expected_interval_s
  const note = top
    ? alertText(station.name, top).headline
    : expected ? `Expected every ${duration(expected * 1000)}` : ''
  return { status: alerts.some((a) => a.kind === 'silent') ? 'no_data' : 'receiving', note }
}

/** The status colour beside the words (DESIGN_GUIDELINE section 2.6): a
 *  station the monitor calls silent is an Alarm; one that is not collected
 *  at all (decommissioned, collection off, never read) is the guideline's
 *  "No data / stale" grey, since nothing is failing there. */
export function statusTone(status: SystemStatus): 'normal' | 'alarm' | 'stale' {
  if (status.status === 'receiving') return 'normal'
  if (status.status === 'not_checked') return 'stale'
  return [DECOMMISSIONED, COLLECTION_OFF, NEVER_READ].includes(status.note) ? 'stale' : 'alarm'
}

/** The words for a system status. */
export function statusWord(status: SystemStatus): string {
  return { receiving: 'Receiving', no_data: 'No data', not_checked: MONITORING_DELAYED }[status.status]
}

/** The Last Observation a station's row shows, and its freshness: the
 *  monitor's own newest real reading (`newest_reading_at`), since
 *  `last_measurement_at` also counts link telemetry, which keeps arriving
 *  from a gateway whose readings have stopped and would hold it on time
 *  until the station went silent, so Late could never show. While the
 *  monitor calls the station silent, the silent alert's reading, overdue.
 *  No verdict while the checks have stopped (they cannot vouch for it) or
 *  without the monitor's numbers. */
export function lastObservation(station: Station, now: Date,
                                checksStale = false): { at: string | null; fresh: Freshness | null } {
  const silent = (station.alerts ?? []).find((a) => a.kind === 'silent')
  if (silent && silent.kind === 'silent') {
    // Silent longer than the monitor looks back (30 days), the alert names
    // no reading: the newest stored one stands in, rather than "Never".
    const at = silent.detail.last_reading_at ?? station.last_measurement_at
    return { at, fresh: at ? 'over' : 'never' }
  }
  const at = station.newest_reading_at ?? station.last_measurement_at
  if (checksStale) return { at, fresh: null }
  const { expected_interval_s: expected, silent_after_s: silentAfter } = station
  return { at, fresh: expected && silentAfter ? freshness(at, expected, silentAfter, now) : null }
}
